import { theme, isProgramsEnabled, isHCFormsEnabled } from "@/services/config";

export const USER_ROLES = {
  ADMIN: {
    id: "000000000000000000000000",
    name: "Administrador",
    key: "admin",
  },
  MANAGER: {
    id: "000000000000000000000001",
    name: "Manager",
    key: "manager",
    alias: "Coordinador",
  },
  DOCTOR: { id: "000000000000000000000002", name: "Doctor", key: "doctor" },
  PATIENT: { id: "000000000000000000000003", name: "Paciente", key: "patient" },
  ASSISTANT: {
    id: "000000000000000000000004",
    name: "Asistente",
    key: "assistant",
  },
  SCORES: {
    id: "000000000000000000000005",
    name: "Scores",
    key: "scores",
  },
  ADMISSIONIST: {
    id: "000000000000000000000006",
    name: "Admisionista",
    key: "admissionist",
  },
  SECRETARY: {
    id: "000000000000000000000007",
    name: "Secretaria",
    key: "secretary",
  },
  HEALTH_ADMIN: {
    id: "000000000000000000000008",
    name: "Administrativo",
    key: "health-admin",
    alias: "Administrativo Financiador",
  },
};

export const DASHBOARD_PERMISSIONS = {
  USER_VIEW: "user.view",
  TREATMENT_VIEW: "treatment.view",
  PATIENT_VIEW: "patient.view",
  ALERT_VIEW: "alert.view",
};

export const TASKS_TYPES = {
  vitals: {
    type: "vitals",
    name: "Registros",
    icon: "favorite",
    hasConfig: true,
    hasMedia: false,
  },
  reports: {
    type: "reports",
    name: "Reportes",
    icon: "local_pharmacy",
    hasConfig: true,
    hasMedia: false,
  },
  appointments: {
    type: "appointments",
    name: "Recordatorios",
    icon: "perm_contact_calendar",
    hasConfig: true,
    hasMedia: false,
  },
  checkins: {
    type: "checkins",
    name: "Check-in",
    icon: "location_on",
    hasConfig: true,
    hasMedia: false,
  },
  recommendations: {
    type: "recommendations",
    name: "Recomendaciones",
    icon: "healing",
    hasConfig: true,
    hasMedia: true,
  },
  exercises: {
    type: "exercises",
    name: "Ejercicios",
    icon: "fitness_center",
    hasConfig: true,
    hasMedia: true,
  },
  medications: {
    type: "medications",
    name: "Medicación",
    icon: "loyalty",
    hasConfig: true,
    hasMedia: false,
  },
  // UC-REG-061: Conversación abierta conducida por IA con score 1-10 +
  // semáforo. Modelo separado de CheckinTask. Detrás del feature flag
  // `monitoring.healthConversation`.
  // icon: 'forum' (verificado en src/components/icons/forum.js).
  healthConversations: {
    type: "healthConversations",
    name: "Conversación de salud",
    icon: "forum",
    hasConfig: true,
    hasMedia: false,
    featureFlag: "monitoring.healthConversation",
  },
};

export const EVENTS_TYPES = {
  "requires-new-service": {
    friendlyName: "Requiere nuevo servicio",
    status: "requires-new-service",
    // only the assistant can mark it as completed
    completedAt: null,
  },
  "patient-reviewed-with-contact": {
    friendlyName:
      theme === "albya" ? "Nota de Ingreso" : "Paciente revisado con llamado",
    status: "patient-reviewed-with-contact",
  },
  "patient-reviewed-without-contact": {
    friendlyName:
      theme === "albya" ? "Nota de Evolución" : "Paciente revisado sin llamado",
    status: "patient-reviewed-without-contact",
  },
  closed: {
    friendlyName: theme === "albya" ? "Nota de Egreso" : "Cerrado",
    status: "closed",
    options: [
      "Alta",
      "Derivado internación",
      "Socio no acepta monitoreo",
      "Óbito",
      "Sin criterio de monitoreo",
    ],
  },
};

const ALL_INDICATIONS_TYPES = [
  {
    type: "program",
    name: "Programas",
    micon: "assignment",
    defaultValues: {
      program: {
        friendlyName: "Programa",
        value: "",
        inputType: "programSelect",
        required: true,
      },
    },
  },
  {
    type: "evolution",
    name: "Evolución clínica",
    micon: "patient_list",
    defaultValues: {
      consultation: {
        friendlyName: "Motivo de consulta",
        value: "",
        inputType: "consultationSelect",
        required: true,
      },
      publicConsultation: {
        friendlyName: "Motivo de consulta manual",
        placeholder:
          "Escriba el motivo de consulta aquí si no lo encuentra en la lista.",
        value: "",
        inputType: "input",
      },
      diagnosis: {
        friendlyName: "Diagnóstico",
        placeholder:
          "Seleccione el diagnóstico aquí si no lo encuentra en la lista.",
        value: "",
        inputType: "diagnosisSelect",
      },
      publicDiagnosis: {
        friendlyName: "Diagnóstico manual",
        placeholder:
          "Escriba el diagnóstico aquí si no lo encuentra en la lista.",
        value: "",
        inputType: "input",
      },
      description: {
        friendlyName: "Evolución clínica",
        placeholder: "Escriba una descripción de la evolución clínica",
        value: "",
        inputType: "textarea",
        required: true,
      },
    },
  },
  {
    type: "medications",
    name: "Prescripción de medicamento",
    micon: "pill",
    allowMultiple: true,
    defaultValues: {
      medication: {
        value: "",
        friendlyName: "Medicamento",
        inputType: "medicationSelect",
        required: true,
      },
      quantity: {
        friendlyName: "Cantidad",
        // UC-REG-095: default 1 (mínimo prescribible). El médico puede
        // cambiarlo. Antes era "" lo que forzaba a tipear cada vez.
        value: 1,
        min: 1,
        inputType: "number",
        required: true,
      },
      generic: {
        friendlyName: "Generico",
        value: "No",
        inputType: "select",
        options: [
          { label: "Sí", value: "Sí" },
          { label: "No", value: "No" },
        ],
      },
      dosage: {
        friendlyName: "Posologia",
        value: "",
        inputType: "textarea",
      },
      isLongTerm: {
        friendlyName: "Tratamiento prolongado",
        value: "No",
        inputType: "select",
        options: [
          { label: "Sí", value: "Sí" },
          { label: "No", value: "No" },
        ],
      },
      diagnosis: {
        value: "",
        friendlyName: "Diagnóstico",
        inputType: "diagnosisSelect",
        required: true,
      },
      observations: {
        friendlyName: "Observaciones",
        value: "",
        inputType: "textarea",
      },
      patientEmail: {
        friendlyName: "Email del paciente",
        value: "",
        inputType: "input",
        required: false,
      },
      //      posdatedTitle: {
      //        friendlyName: "Recetas posdatadas",
      //        value: "",
      //        inputType: "title",
      //      },
      //      quantityPosdated: {
      //        friendlyName: "Cantidad",
      //        value: "",
      //        inputType: "number",
      //      },
      //      dayPosdated: {
      //        friendlyName: "Dias",
      //        value: "",
      //        inputType: "number",
      //      },
      //      datePosdated: {
      //        friendlyName: "Fechas",
      //        value: [],
      //        inputType: "multiDate",
      //        required: false,
      //      },
    },
  },
  // {
  //   type: "indications",
  //   name: "Indicaciones",
  //   defaultValues: {},
  // },
  {
    type: "images",
    name: "Orden de imágen",
    micon: "radiology",
    // UC-REG-147: multi-orden como medications. Federico 2026-07-01.
    allowMultiple: true,
    defaultValues: {
      diagnosis: {
        friendlyName: "Diagnóstico",
        value: "",
        inputType: "diagnosisSelect",
      },
      image: {
        friendlyName: "Imágen",
        value: "",
        inputType: "imagesSelect",
        required: true,
      },
      observation: {
        friendlyName: "Observaciones",
        value: "",
        inputType: "textarea",
      },
      // UC-REG-147: email del paciente por sección con sync via event
      // bus entre instances (ver Indications.vue watch/created).
      patientEmail: {
        friendlyName: "Email del paciente",
        value: "",
        inputType: "input",
        required: false,
      },
    },
  },
  {
    type: "lab",
    name: "Orden de laboratorio",
    micon: "labs",
    // UC-REG-147: multi-orden como medications. Federico 2026-07-01.
    allowMultiple: true,
    defaultValues: {
      diagnosis: {
        friendlyName: "Diagnóstico",
        value: "",
        inputType: "diagnosisSelect",
      },
      labs: {
        friendlyName: "Determinación",
        value: [],
        inputType: "labSelect",
        required: true,
      },
      observation: {
        friendlyName: "Observaciones",
        value: "",
        inputType: "textarea",
      },
      patientEmail: {
        friendlyName: "Email del paciente",
        value: "",
        inputType: "input",
        required: false,
      },
    },
  },
  {
    type: "practices",
    name: "Prácticas",
    micon: "assignment",
    defaultValues: {
      diagnosis: {
        friendlyName: "Diagnóstico",
        value: "",
        inputType: "diagnosisSelect",
      },
      description: {
        friendlyName: "Descripción",
        value: "",
        inputType: "textarea",
        required: true,
      },
      patientEmail: {
        friendlyName: "Email del paciente",
        value: "",
        inputType: "input",
        required: false,
      },
    },
  },
  {
    type: "imagesResults",
    name: "Resultados de imágenes",
    micon: "assignment",
    defaultValues: {
      diagnosis: {
        friendlyName: "Diagnóstico",
        value: "",
        inputType: "diagnosisSelect",
      },
      description: {
        friendlyName: "Descripción",
        value: "",
        inputType: "textarea",
      },
    },
  },
  {
    type: "labResults",
    name: "Resultados de laboratorio",
    micon: "assignment",
    defaultValues: {
      diagnosis: {
        friendlyName: "Diagnóstico",
        value: "",
        inputType: "diagnosisSelect",
      },
      description: {
        friendlyName: "Descripción",
        value: "",
        inputType: "textarea",
      },
    },
  },
  // {
  //   type: "diagnosis",
  //   name: "Diagnóstico",
  //   defaultValues: {},
  // },
  {
    type: "notes",
    name: "Notas",
    micon: "inventory",
    defaultValues: {
      description: {
        friendlyName: "Motivo",
        value: "",
        inputType: "input",
        required: true,
      },
      observation: {
        friendlyName: "Descripción",
        value: "",
        inputType: "textarea",
        required: true,
      },
    },
  },
  {
    type: "certificates",
    name: "Certificados",
    micon: "inventory",
    defaultValues: {
      description: {
        friendlyName: "Motivo",
        value: "",
        inputType: "input",
        required: true,
      },
      observation: {
        friendlyName: "Descripción",
        value: "",
        inputType: "textarea",
        required: true,
      },
    },
  },
  {
    type: "forms",
    name: "Formularios",
    micon: "inventory",
    defaultValues: {
      form: {
        friendlyName: "Seleccione un formulario",
        value: "",
        inputType: "formSelect",
      },
    },
  },
  // {
  //   type: "epicrisis",
  //   name: "Epicrisis",
  //   defaultValues: {},
  // },
];

export const INDICATIONS_TYPES = ALL_INDICATIONS_TYPES.filter((item) => {
  if (item.type === "program") {
    return isProgramsEnabled;
  }
  if (item.type === "forms") {
    return isHCFormsEnabled;
  }
  return true;
});

export const PROVINCES = [
  "Buenos Aires",
  "CABA (Cuidad autónoma de Buenos Aires)",
  "Catamarca",
  "Chaco",
  "Chubut",
  "Córdoba",
  "Corrientes",
  "Entre Ríos",
  "Formosa",
  "Jujuy",
  "La Pampa",
  "La Rioja",
  "Mendoza",
  "Misiones",
  "Neuquén",
  "Río Negro",
  "Salta",
  "San Juan",
  "San Luis",
  "Santa Cruz",
  "Santa Fe",
  "Santiago del Estero",
  "Tierra del Fuego",
  "Tucumán",
];

export const APPOINTMENTS_TYPES_VADIGU = {
  // inPerson: { name: "Presencial", code: 1 },
  virtual: { name: "Virtual", code: 2 },
};

export const APPOINTMENTS_TYPES = {
  "in-person": { value: "in-person", friendlyName: "Presencial", code: 1 },
  virtual: { value: "virtual", friendlyName: "Virtual", code: 2 },
};

export const APPOINTMENTS_STATUS = {
  all: "Todas las citas",
  free: "Disponibles",
  busy: "Ocupados",
};

// Default tasks data

const media = {
  type: "image",
  content: [],
};

const basicConfig = {
  name: "",
  description: "",
  tags: [],
  measurement: {
    green: null,
    yellow: null,
    red: null,
  },
  links: [],
  shouldAlert: true,
};

const periodizationConfig = {
  type: "range",
  days: [],
  fromDay: 1,
  toDay: 30,
  hours: [],
  shouldNotify: true,
};

const recommendationDefault = {
  ...basicConfig,
  periodization: periodizationConfig,
  media,
  isFeatured: true,
  category: "",
};

const medicationDefault = {
  ...basicConfig,
  periodization: periodizationConfig,
  quantity: 0,
};

const exerciseDefault = {
  ...basicConfig,
  periodization: periodizationConfig,
  repetitions: 0,
  duration: "",
  series: 0,
  distance: 0,
  weight: 0,
  media,
};

const appointmentDefault = {
  ...basicConfig,
  periodization: periodizationConfig,
  category: "doctor",
};

const reportsDefault = {
  ...basicConfig,
  completedMeasurement: "yellow",
  isEditable: false,
  recommendations: [],
};

const checkinsDefault = {
  ...basicConfig,
  periodization: periodizationConfig,
  answerValues: [
    {
      question: "",
      answers: [
        {
          measurement: "green",
          type: "text",
          content: "",
        },

        {
          measurement: "red",
          type: "text",
          content: "",
        },
      ],
    },
  ],
};

const vitalsDefault = {
  ...basicConfig,
  periodization: periodizationConfig,
  answerValues: [
    {
      measurement: {
        green: {
          min: null,
          max: null,
        },
        yellow: {
          min: null,
          max: null,
        },
        red: {
          min: null,
          max: null,
        },
      },
    },
  ],
};

/**
 * @description Default para HealthConversationTask (UC-REG-061). El médico
 *              parte de una plantilla — el form carga la lista y al elegir
 *              hidrata estos campos. Acá solo necesitamos shape inicial
 *              razonable para que el form no rompa antes de la selección.
 */
const healthConversationsDefault = {
  ...basicConfig,
  periodization: periodizationConfig,
  templateKey: "",
  objective: "",
  evaluationCriteria: [],
  scoreThresholds: {
    green: [1, 3],
    yellow: [4, 7],
    red: [8, 10],
  },
  maxTurns: 5,
  patientTriggerable: false,
};

const defaultTasksData = {
  appointments: appointmentDefault,
  recommendations: recommendationDefault,
  medications: medicationDefault,
  exercises: exerciseDefault,
  reports: reportsDefault,
  checkins: checkinsDefault,
  vitals: vitalsDefault,
  healthConversations: healthConversationsDefault,
};

export function getDefaultTaskData(type) {
  const defaultConfig = defaultTasksData[type] || basicConfig;
  // Return object by value instead of reference
  return JSON.parse(JSON.stringify(defaultConfig));
}

export function getIndicationForm(type) {
  const entry = INDICATIONS_TYPES.find((i) => i.type === type);
  if (!entry?.defaultValues) return [];
  return Object.entries(entry.defaultValues).map(([label, cfg]) => ({
    label,
    ...cfg,
  }));
}

export const DOC_TYPES =
  theme === "dash"
    ? ["curp"]
    : ["ci", "dni", "iibb", "lc", "le", "pas", "usr", "var"];

export const DOC_TYPE_DEFAULT = theme === "dash" ? "curp" : "dni";

export const TAX_ID_TYPES = ["cuil", "cuit"];

export const LANGUAGES = [
  "Árabe",
  "Bengalí",
  "Chino Mandarín",
  "Español",
  "Francés",
  "Hindi",
  "Indonesio",
  "Inglés",
  "Portugués",
  "Ruso",
];

export const WEEK_DAYS_OPTIONS = [
  { name: "Domingo", value: 0 },
  { name: "Lunes", value: 1 },
  { name: "Martes", value: 2 },
  { name: "Miércoles", value: 3 },
  { name: "Jueves", value: 4 },
  { name: "Viernes", value: 5 },
  { name: "Sábado", value: 6 },
];

export const SLOT_ATTENTION_TYPES = [
  { name: "Virtual", value: "virtual" },
  { name: "Presencial", value: "in-person" },
];

export const SLOT_PERIODICITY = [
  { value: "weekly", name: "Semanalmente" },
  { value: "every-2-weeks", name: "Cada 2 semanas" },
  { value: "every-3-weeks", name: "Cada 3 semanas" },
  { value: "first-of-month", name: "Primero del mes" },
  { value: "second-of-month", name: "Segundo del mes" },
  { value: "third-of-month", name: "Tercero del mes" },
  { value: "fourth-of-month", name: "Cuarto del mes" },
  { value: "fifth-of-month", name: "Quinto del mes" },
];

export const massiveActionsTypes = [
  { value: "patient-creation", label: "Alta masiva de pacientes" },
  { value: "treatment-assignation", label: "Asignación masiva de protocolos" },
  { value: "recommendation-creation", label: "Alta masiva de recomendaciones" },
];

export const massiveActionsStatuses = [
  { value: "waiting", label: "En espera" },
  { value: "in-progress", label: "En progreso" },
  { value: "ended", label: "Finalizado" },
];
export const VIDEOROOM_MM_OFFSET_PRE = 60; // EXPRESSED IN MINUTES
export const VIDEOROOM_MM_OFFSET_POST = 240; // EXPRESSED IN MINUTES

// Motivos de consultas viejos
// export const CONSULTATION_OPTIONS = [
//   "Consulta médica",
//   "Pedido receta",
//   "Seguimiento tratamiento",
//   "Consulta inicial",
//   "Consulta seguimiento",
//   "Cierre tratamiento",
// ];

// Motivos de consultas nuevos
export const CONSULTATION_OPTIONS = [
  "HTA",
  "Disnea",
  "Palpitaciones",
  "Mareos",
  "Dolor torácico",
  "Pérdida de conocimiento",
  "Edema de MMII",
  "Evaluación prequirúrgica",
  "Control de marcapasos",
  "Tos y dificultad respiratoria",
  "Dolor abdominal",
  "Vómitos y diarrea",
  "Dolor de oído/garganta",
  "Broncoespasmo",
  "Otitis",
  "Faringitis",
  "Bronquiolitis",
  "Lesiones en piel",
  "Fiebre",
  "Amenaza de parto prematuro",
  "Celiaquia + control embarazo",
  "Control de embarazo",
  "Hemorragia en el embarazo",
  "Medicación prenatal",
  "Seguimiento de embarazo de alto riesgo",
  "Otros",
  "Rutina",
  "Flujo vaginal",
  "Dolor pélvico",
  "Trastornos menstruales (amenorrea, metrorragia, dismenorrea, hipermenorrea)",
  "Sospecha o control de embarazo",
  "Anticoncepción",
  "Menopausia/síntomas de climaterio",
  "Lesión de mama o dolor mamario",
  "ITU",
  "Resultados de PAP o estudios previos",
  "Hipotiroidismo",
  "Osteoporosis",
  "Amenorrea secundaria",
  "Nódulo suprarrenal",
  "Hiperparotiditis",
  "Menopausia",
  "DBT",
  "Resistencia a la insulina",
  "Disfunción eréctil",
  "Artrosis",
  "Cervicalgia",
  "Control de resonancia",
  "Control ortopédico",
  "Desgarro bilateral",
  "Desgaste articular",
  "Dificultad en la marcha",
  "Dolor de hombro",
  "Dolor lumbar/cervical",
  "Dolor plantar",
  "Dolor rodilla derecha",
  "Escoliosis",
  "Esguinces",
  "Fracturas",
  "Lesiones deportivas",
  "Lumbalgia",
  "Lumbociática",
  "Luxaciones",
  "Omalgia",
  "Osteocondritis",
  "Rectificación",
  "Ruptura tendinosa",
  "Síndrome meniscal",
  "Sospechas de fracturas",
  "Tendinitis",
  "Trocanteritis",
];

export const CONSULTATION_OPTIONS_CNC = ["Consulta médica"];

// Patient Admission Status for Appointments
export const PATIENT_ADMISSION_STATUS = {
  pending: {
    label: "No Admitido",
    value: "pending",
    buttonClass: "button-gray-new",
  },

  admitted: {
    label: "En sala",
    value: "admitted",
    buttonClass: "button-red-new",
  },

  started: {
    label: "En Curso",
    value: "started",
    buttonClass: "button-green-new",
  },

  done: {
    label: "Atendido",
    value: "done",
    buttonClass: "button-green-new",
  },
};

export function getPatientAdmissionStatus(status) {
  if (!status) return PATIENT_ADMISSION_STATUS.pending;
  return PATIENT_ADMISSION_STATUS[status] || PATIENT_ADMISSION_STATUS.pending;
}
